import amazonIcon from '../components/icons/amazon.svg';
import sallaIcon from '../components/icons/salla.svg';
import zidIcon from '../components/icons/zid.svg';
import noonIcon from '../components/icons/noon.svg';
import trendyolIcon from '../components/icons/trendyol.svg';

export const DEFAULT_MARKETPLACE_ICON = '/assets/loops-logo1.png';
export const SELLERHUB_MARKETPLACE_ICON = DEFAULT_MARKETPLACE_ICON;

const S3_BASE = 'https://loops-marketplace-logo.s3.eu-west-1.amazonaws.com';

/** Local bundled icons — always reliable for known marketplaces. */
const LOCAL_ICON_MAP = {
  amazon: amazonIcon,
  salla: sallaIcon,
  zid: zidIcon,
  trendyol: trendyolIcon,
  noon: noonIcon,
  sellerhub: SELLERHUB_MARKETPLACE_ICON,
};

/** Canonical remote SVG assets (aligned with backend marketplaceLogos util). */
const REMOTE_ICON_MAP = {
  amazon: `${S3_BASE}/amazon-ic.svg`,
  salla: `${S3_BASE}/salla-ic.svg`,
  zid: `${S3_BASE}/zid-ic.svg`,
  trendyol: `${S3_BASE}/trendyol-ic.svg`,
  noon: `${S3_BASE}/noon-ic.svg`,
  sellerhub: SELLERHUB_MARKETPLACE_ICON,
};

const REMOTE_LOGO_MAP = {
  amazon: `${S3_BASE}/amazon.svg`,
  salla: `${S3_BASE}/salla.svg`,
  zid: `${S3_BASE}/zid.svg`,
  trendyol: `${S3_BASE}/trendyol.svg`,
  noon: `${S3_BASE}/noon.svg`,
  sellerhub: SELLERHUB_MARKETPLACE_ICON,
};

function isUserCreatedFlag(value) {
  return value === 1 || value === true || value === '1';
}

function normalizePlatformKey(platformName) {
  if (!platformName || typeof platformName !== 'string') return '';
  const raw = platformName.toLowerCase().trim();
  if (!raw) return '';
  if (LOCAL_ICON_MAP[raw] || REMOTE_LOGO_MAP[raw]) return raw;
  if (raw.includes('amazon')) return 'amazon';
  if (raw.includes('salla')) return 'salla';
  if (raw.includes('zid')) return 'zid';
  if (raw.includes('trendyol')) return 'trendyol';
  if (raw.includes('noon')) return 'noon';
  if (raw.includes('sellerhub') || raw.includes('loops')) return 'sellerhub';
  return raw;
}

function isStaleMarketplacePng(url) {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.includes('loops-marketplace-logo') &&
    (lower.endsWith('.png') || lower.includes('.png?'))
  );
}

/**
 * Normalize a remote icon URL. Prefer services.icon (already square *-ic.svg).
 * Stale S3 .png logo URLs are replaced with the known -ic.svg set.
 */
export function normalizeMarketplaceIconUrl(url, platformName) {
  const key = normalizePlatformKey(platformName);
  if (url && !isStaleMarketplacePng(url)) {
    return url;
  }
  return REMOTE_ICON_MAP[key] || REMOTE_LOGO_MAP[key] || url || null;
}

/** @deprecated use normalizeMarketplaceIconUrl — logo assets are not used for dropdowns */
export function normalizeMarketplaceLogoUrl(url, platformName) {
  return normalizeMarketplaceIconUrl(url, platformName);
}

/**
 * Resolve marketplace icon for lists/filters.
 * Priority:
 * 0. User-created platforms → always SellerHub logo
 * 1. platformLogos map (from get-source-list / services.icon)
 * 2. platform.icon_url then logo_url
 * 3. Local bundled SVG
 * 4. Remote *-ic.svg fallback
 */
export function resolveMarketplaceIcon(
  platformName,
  platformLogos = {},
  platform = null
) {
  const key = normalizePlatformKey(platformName);
  if (!key && !platform) {
    return null;
  }

  // User-created platforms must never map to system marketplace icons
  if (
    isUserCreatedFlag(platform?.is_user_created) ||
    key === 'user_created' ||
    String(platformName || '')
      .toLowerCase()
      .includes('user_created')
  ) {
    return SELLERHUB_MARKETPLACE_ICON;
  }

  // Prefer API icon map (master services.icon via get-source-list / dropdowns)
  const logoCandidates = [
    key,
    platformName?.toLowerCase?.(),
    platform?.name?.toLowerCase?.(),
    platform?.display_name?.toLowerCase?.(),
  ].filter(Boolean);

  for (const candidate of logoCandidates) {
    if (platformLogos[candidate]) {
      const normalized = normalizeMarketplaceIconUrl(
        platformLogos[candidate],
        candidate
      );
      if (normalized) return normalized;
    }
  }

  // Platform row from dropdowns/all
  const fromPlatform =
    normalizeMarketplaceIconUrl(
      platform?.icon_url || platform?.logo_url,
      platform?.name || platformName || key
    ) || null;
  if (fromPlatform) {
    return fromPlatform;
  }

  // Local bundled SVGs for known marketplaces
  if (LOCAL_ICON_MAP[key]) {
    return LOCAL_ICON_MAP[key];
  }

  // Last remote fallback (icons, not logos)
  return REMOTE_ICON_MAP[key] || REMOTE_LOGO_MAP[key] || null;
}

export function buildPlatformLogosFromSourceList(sourceList = []) {
  const logos = {};

  sourceList.forEach((item) => {
    const keys = [item.source, item.name]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());
    const logo = isUserCreatedFlag(item.is_user_created)
      ? SELLERHUB_MARKETPLACE_ICON
      : normalizeMarketplaceLogoUrl(item.logo, item.name || item.source);
    if (!logo) return;
    keys.forEach((key) => {
      logos[key] = logo;
      const normalized = normalizePlatformKey(key);
      if (normalized) logos[normalized] = logo;
    });
  });

  return logos;
}
